import { useState, useMemo, useCallback } from "react";
import { usePagination } from "../../../../hooks/usePagination";
import { COLLECTIONS } from "../../../../services/collections";
import { PAGE_SIZE } from "../constants";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

const today = () => new Date().toISOString().split("T")[0];

export const usePayablesRecords = (idTenant) => {
    // Filtros
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate] = useState(today());
    
    // Estados internos aplicados (para evitar busca instantânea ao digitar)
    const [appliedStartDate, setAppliedStartDate] = useState(today());
    const [appliedEndDate, setAppliedEndDate] = useState(today());

    // Filtros do Firestore
    const firestoreFilters = useMemo(() => {
        const filters = [
            { field: "type", operator: "==", value: FINANCIAL_TYPES.LIABILITY },
            { field: "dueDate", operator: ">=", value: appliedStartDate },
            { field: "dueDate", operator: "<=", value: appliedEndDate },
        ];
        if (statusFilter) {
            filters.push({ field: "status", operator: "==", value: statusFilter });
        }
        return filters;
    }, [statusFilter, appliedStartDate, appliedEndDate]);

    const applyDateFilters = useCallback((overrideStart, overrideEnd) => {
        // Proteger contra receber evento de click (SyntheticEvent) como argumento
        const validStart = typeof overrideStart === "string" ? overrideStart : null;
        const validEnd = typeof overrideEnd === "string" ? overrideEnd : null;

        const newStart = validStart || startDate;
        const newEnd = validEnd || endDate;
        if (validStart) setStartDate(validStart);
        if (validEnd) setEndDate(validEnd);
        setAppliedStartDate(newStart);
        setAppliedEndDate(newEnd);
    }, [startDate, endDate]);

    // Paginação Genérica do Firestore
    const {
        data: records,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
    } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.FINANCIAL_RECORDS}`,
        idTenant,
        {
            pageSize: PAGE_SIZE,
            orderField: "dueDate",
            orderDirection: "desc",
            filters: firestoreFilters,
        }
    );

    // Busca textual local
    const filteredRecords = useMemo(() => {
        if (!search.trim()) return records;
        const term = search.toLowerCase().trim();
        return records.filter(r =>
            (r.description || "").toLowerCase().includes(term) ||
            (r.entityName || "").toLowerCase().includes(term) ||
            (r.categoryName || "").toLowerCase().includes(term)
        );
    }, [records, search]);

    // Totais do período carregado
    const summary = useMemo(() => {
        let totalPending = 0;
        let totalOverdue = 0;
        let totalPaid = 0;
        let countPending = 0;
        let countOverdue = 0;

        records.forEach(r => {
            if (r.status === FINANCIAL_STATUS.PENDING) {
                totalPending += r.grossAmount || 0;
                countPending++;
            } else if (r.status === FINANCIAL_STATUS.OVERDUE) {
                totalOverdue += r.grossAmount || 0;
                countOverdue++;
            } else if (r.status === FINANCIAL_STATUS.PAID) {
                totalPaid += r.grossAmount || 0;
            }
        });

        return { totalPending, totalOverdue, totalPaid, countPending, countOverdue };
    }, [records]);

    return {
        records: filteredRecords,
        allRecords: records,
        summary,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
        statusFilter, setStatusFilter,
        search, setSearch,
        startDate, setStartDate,
        endDate, setEndDate,
        applyDateFilters,
    };
};
